import { useState } from 'react';
import {
  LayoutDashboard, Calendar, Users, FileText, Settings, Bell, Search, Plus,
  ChevronLeft, ChevronRight, Clock, MapPin, Monitor, RotateCcw, DollarSign,
  Stethoscope, MessageCircle, MoreHorizontal, TrendingUp, Activity
} from 'lucide-react';

/**
 * AppMockupsPage — Premium design system mockup
 * Aplicação visual do design_guidelines.json — sistema "Organic & Earthy"
 * Paleta: Deep Forest Green (#1C3F30), warm off-white (#FDFDFD), stone gray
 * Tipografia: Outfit (heading) + Manrope (body) via Google Fonts
 */

const FONT_LINK = (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Manrope:wght@400;500;600;700&display=swap');
    .font-heading { font-family: 'Outfit', system-ui, sans-serif; }
    .font-body { font-family: 'Manrope', system-ui, sans-serif; }
    .mockup-root { font-family: 'Manrope', system-ui, sans-serif; color: #171D1A; }
    .mockup-bg { background: #FDFDFD; }
    .text-brand { color: #1C3F30; }
    .bg-brand { background: #1C3F30; }
    .bg-brand-soft { background: #F0F2F0; }
    .border-soft { border-color: #E6E8E6; }
    .shadow-subtle { box-shadow: 0 2px 10px -3px rgba(0,0,0,0.05); }
    .shadow-elevated { box-shadow: 0 8px 30px rgba(0,0,0,0.08); }
    .text-muted { color: #5C6B64; }
    .ring-brand-faint { box-shadow: 0 0 0 1px rgba(28,63,48,0.12); }
  `}</style>
);

const STATUS = {
  agendada: { bg: 'bg-[#FFFBEB]', text: 'text-[#B45309]', ring: 'ring-[#B45309]/20', label: 'Não Confirmada' },
  confirmada: { bg: 'bg-[#ECFDF5]', text: 'text-[#047857]', ring: 'ring-[#047857]/20', label: 'Confirmada' },
  realizada: { bg: 'bg-[#F8FAFC]', text: 'text-[#334155]', ring: 'ring-[#334155]/20', label: 'Realizada' },
  cancelada: { bg: 'bg-[#FEF2F2]', text: 'text-[#B91C1C]', ring: 'ring-[#B91C1C]/20', label: 'Cancelada' },
};

const TIPO = {
  presencial: { bg: 'bg-[#F0F7FF]', text: 'text-[#0369A1]', ring: 'ring-[#0369A1]/20', label: 'Presencial' },
  online: { bg: 'bg-[#EEF2FF]', text: 'text-[#4338CA]', ring: 'ring-[#4338CA]/20', label: 'Online' },
  retorno: { bg: 'bg-[#FFF1F2]', text: 'text-[#BE123C]', ring: 'ring-[#BE123C]/20', label: 'Retorno' },
};

const Avatar = ({ name, size = 'md', tone = 'brand' }) => {
  const initials = name.split(' ').filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase();
  const sizes = { sm: 'w-7 h-7 text-[10px]', md: 'w-9 h-9 text-xs', lg: 'w-12 h-12 text-base' };
  const tones = {
    brand: 'bg-[#F0F2F0] text-[#1C3F30]',
    blue: 'bg-[#F0F7FF] text-[#0369A1]',
    rose: 'bg-[#FFF1F2] text-[#BE123C]',
    indigo: 'bg-[#EEF2FF] text-[#4338CA]',
  };
  return (
    <div className={`${sizes[size]} ${tones[tone]} rounded-full flex items-center justify-center font-semibold flex-shrink-0`}>
      {initials}
    </div>
  );
};

const Badge = ({ tone, children, dot }) => {
  const t = tone || { bg: 'bg-slate-100', text: 'text-slate-600', ring: 'ring-slate-200/40' };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${t.bg} ${t.text} ring-1 ${t.ring}`}>
      {dot && <span className="w-1 h-1 rounded-full bg-current"></span>}
      {children}
    </span>
  );
};

const Sidebar = ({ active = 'agenda' }) => {
  const items = [
    { key: 'dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { key: 'agenda', icon: Calendar, label: 'Agenda' },
    { key: 'pacientes', icon: Users, label: 'Pacientes' },
    { key: 'prontuarios', icon: FileText, label: 'Prontuários' },
    { key: 'configuracoes', icon: Settings, label: 'Configurações' },
  ];
  return (
    <aside className="w-64 bg-white border-r border-soft h-full flex flex-col flex-shrink-0">
      <div className="p-6 border-b border-soft">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-brand flex items-center justify-center">
            <Stethoscope className="w-4 h-4 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-heading text-base font-semibold leading-none">Agenda Mais</div>
            <div className="text-[10px] text-muted tracking-widest uppercase mt-1">Med</div>
          </div>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-0.5">
        {items.map(it => {
          const Icon = it.icon;
          const isActive = it.key === active;
          return (
            <a key={it.key} href="#" className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              isActive ? 'bg-brand text-white' : 'text-[#171D1A] hover:bg-brand-soft'
            }`}>
              <Icon className="w-4 h-4" strokeWidth={1.5} />
              {it.label}
            </a>
          );
        })}
      </nav>
      <div className="p-3 border-t border-soft">
        <div className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-brand-soft transition-colors cursor-pointer">
          <Avatar name="Nathalie Sanches" size="sm" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold truncate">Dra. Nathalie</div>
            <div className="text-[10px] text-muted truncate">Ginecologia</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

const TopBar = ({ title, subtitle }) => (
  <header className="bg-white/80 backdrop-blur-md border-b border-soft px-8 py-4 flex items-center justify-between flex-shrink-0">
    <div>
      <h1 className="font-heading text-2xl font-semibold tracking-tight text-[#171D1A]">{title}</h1>
      {subtitle && <p className="text-sm text-muted mt-0.5">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-3">
      <div className="relative">
        <Search className="w-3.5 h-3.5 text-muted absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
        <input
          type="text"
          placeholder="Buscar paciente, consulta..."
          className="pl-9 pr-3 py-2 text-sm bg-[#FDFDFD] border border-soft rounded-lg w-64 focus:outline-none focus:ring-2 focus:ring-[#1C3F30]/20 focus:border-[#1C3F30] placeholder:text-slate-400"
        />
      </div>
      <button className="p-2 rounded-lg hover:bg-brand-soft transition-colors relative">
        <Bell className="w-4 h-4 text-muted" strokeWidth={1.5} />
        <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-[#BE123C]"></span>
      </button>
    </div>
  </header>
);

// ===================== Mockup 1: DASHBOARD =====================
const DashboardMockup = () => (
  <div className="flex h-full mockup-root mockup-bg">
    <Sidebar active="dashboard" />
    <div className="flex-1 flex flex-col overflow-hidden">
      <TopBar title="Bom dia, Dra. Nathalie" subtitle="quinta-feira, 30 de abril · você tem 8 consultas hoje" />
      <main className="flex-1 overflow-y-auto p-8 space-y-6">
        {/* Bento metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { icon: Calendar, label: 'Consultas hoje', value: '8', delta: '+2 vs. ontem', tone: 'text-[#047857]' },
            { icon: Users, label: 'Pacientes ativos', value: '342', delta: 'esse mês', tone: 'text-muted' },
            { icon: TrendingUp, label: 'Receita do mês', value: 'R$ 28.450', delta: '+12.4%', tone: 'text-[#047857]' },
          ].map((m, i) => {
            const Icon = m.icon;
            return (
              <div key={i} className="bg-white border border-soft rounded-xl p-5 shadow-subtle hover:shadow-elevated transition-all hover:-translate-y-px">
                <div className="flex items-start justify-between mb-3">
                  <div className="w-9 h-9 bg-brand-soft rounded-lg flex items-center justify-center">
                    <Icon className="w-4 h-4 text-brand" strokeWidth={1.5} />
                  </div>
                  <MoreHorizontal className="w-4 h-4 text-muted" strokeWidth={1.5} />
                </div>
                <div className="font-heading text-3xl font-semibold tracking-tight">{m.value}</div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] text-muted uppercase tracking-wider font-bold">{m.label}</span>
                  <span className={`text-xs font-semibold ${m.tone}`}>· {m.delta}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Próximas consultas */}
        <div className="bg-white border border-soft rounded-xl shadow-subtle">
          <div className="px-6 py-4 border-b border-soft flex items-center justify-between">
            <div>
              <h2 className="font-heading text-lg font-semibold">Próximas Consultas</h2>
              <p className="text-xs text-muted">Hoje · 5 confirmadas, 3 aguardando</p>
            </div>
            <a href="#" className="text-xs font-semibold text-brand hover:underline">Ver agenda completa →</a>
          </div>
          <div className="divide-y divide-[#E6E8E6]">
            {[
              { hora: '09:00', nome: 'Beatriz Mendes Oliveira', tipo: 'presencial', status: 'confirmada', proc: 'Consulta Ginecológica', valor: 'R$ 320' },
              { hora: '10:30', nome: 'Carla Souza Lima', tipo: 'online', status: 'agendada', proc: 'Reposição Hormonal' },
              { hora: '14:00', nome: 'Helena Martins Barbosa', tipo: 'retorno', status: 'confirmada', proc: 'Retorno · Consulta' },
              { hora: '15:30', nome: 'Mariana Dias Cardoso', tipo: 'presencial', status: 'confirmada', proc: 'Preventivo', valor: 'R$ 250' },
            ].map((c, i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-3.5 hover:bg-[#FDFDFD] cursor-pointer transition-colors">
                <div className="font-heading text-sm font-bold text-[#171D1A] tabular-nums w-12">{c.hora}</div>
                <Avatar name={c.nome} size="md" tone={c.tipo === 'online' ? 'indigo' : c.tipo === 'retorno' ? 'rose' : 'blue'} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{c.nome}</div>
                  <div className="text-xs text-muted truncate">{c.proc}</div>
                </div>
                <Badge tone={TIPO[c.tipo]}>{TIPO[c.tipo].label}</Badge>
                <Badge tone={STATUS[c.status]} dot>{STATUS[c.status].label}</Badge>
                {c.valor && <span className="text-xs font-bold text-[#047857] tabular-nums w-16 text-right">{c.valor}</span>}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  </div>
);

// ===================== Mockup 2: AGENDA =====================
const AgendaMockup = () => (
  <div className="flex h-full mockup-root mockup-bg">
    <Sidebar active="agenda" />
    <div className="flex-1 flex flex-col overflow-hidden">
      <TopBar title="Agenda" subtitle="Abril 2026 · Semana 17" />
      <main className="flex-1 overflow-y-auto p-8">
        {/* Toolbar */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <button className="px-3 py-1.5 text-sm font-medium border border-soft bg-white rounded-lg hover:bg-brand-soft">Hoje</button>
            <button className="p-1.5 rounded-lg hover:bg-brand-soft text-muted"><ChevronLeft className="w-5 h-5" strokeWidth={1.5}/></button>
            <button className="p-1.5 rounded-lg hover:bg-brand-soft text-muted"><ChevronRight className="w-5 h-5" strokeWidth={1.5}/></button>
            <h2 className="font-heading text-lg font-semibold ml-2">23 abr 2026</h2>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex bg-brand-soft rounded-lg p-1">
              <button className="px-3 py-1 text-xs font-semibold rounded-md bg-white text-brand shadow-sm">Semana</button>
              <button className="px-3 py-1 text-xs font-semibold rounded-md text-muted">Dia</button>
            </div>
            <button className="bg-brand text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#275340] flex items-center gap-1.5 shadow-sm">
              <Plus className="w-4 h-4" strokeWidth={1.5}/>
              Nova Consulta
            </button>
          </div>
        </div>

        {/* Calendar grid */}
        <div className="bg-white border border-soft rounded-xl overflow-hidden shadow-subtle">
          <div className="grid grid-cols-7 border-b border-soft bg-[#FDFDFD]">
            {['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SÁB'].map((d, i) => {
              const isSelected = d === 'QUI';
              const date = 19 + i;
              return (
                <div key={d} className={`p-3 text-center border-r last:border-r-0 border-soft ${isSelected ? 'bg-white' : ''}`}>
                  <p className="text-[10px] text-muted uppercase tracking-wider font-bold">{d}</p>
                  <p className={`font-heading text-xl font-semibold mt-1 ${isSelected ? 'bg-brand text-white w-7 h-7 rounded-full flex items-center justify-center mx-auto' : ''}`}>
                    {date}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="grid grid-cols-7 min-h-[420px]">
            {[null, null, null, null, 'selected', null, null].map((mode, i) => (
              <div key={i} className={`border-r last:border-r-0 border-soft p-2 space-y-2 ${mode === 'selected' ? 'bg-[#FDFDFD]' : ''}`}>
                {mode === 'selected' ? [
                  { hora: '09:00', nome: 'Beatriz Mendes', tipo: 'presencial', status: 'confirmada' },
                  { hora: '10:30', nome: 'Carla Souza', tipo: 'online', status: 'agendada' },
                  { hora: '14:00', nome: 'Helena Martins', tipo: 'retorno', status: 'confirmada' },
                ].map((c, j) => (
                  <div key={j} className={`${TIPO[c.tipo].bg} ring-1 ${TIPO[c.tipo].ring} rounded-xl p-3 cursor-pointer hover:shadow-subtle transition-all`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-bold tabular-nums text-[#171D1A]">{c.hora}</span>
                      <Badge tone={STATUS[c.status]} dot>{STATUS[c.status].label}</Badge>
                    </div>
                    <div className="text-sm font-semibold mt-1.5 text-[#171D1A]">{c.nome}</div>
                    <div className="text-[10px] text-muted mt-0.5">Consulta Ginecológica</div>
                  </div>
                )) : Array.from({ length: 2 + (i % 3) }).map((_, j) => {
                  const names = ['Laura F.', 'Gabriela R.', 'Isabela N.', 'Mariana D.', 'Daniela C.'];
                  return (
                    <div key={j} className="bg-[#F8FAFC] ring-1 ring-slate-200/60 rounded-lg px-2 py-1.5 text-[10px]">
                      <div className="font-bold tabular-nums text-muted">{`${8 + j}:00`}</div>
                      <div className="font-semibold text-[#171D1A] text-[11px] mt-0.5">{names[(i + j) % 5]}</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  </div>
);

// ===================== Mockup 3: DETALHES CONSULTA =====================
const DetalhesMockup = () => (
  <div className="flex h-full mockup-root mockup-bg">
    <Sidebar active="agenda" />
    <div className="flex-1 flex flex-col overflow-hidden">
      <TopBar title="Beatriz Mendes Oliveira" subtitle="Consulta · 30/04/2026 · 09:00" />
      <main className="flex-1 overflow-y-auto p-8 grid grid-cols-3 gap-6">
        {/* Coluna esquerda: paciente + clinical */}
        <div className="col-span-2 space-y-5">
          {/* Hero card */}
          <div className="bg-white border border-soft rounded-xl p-6 shadow-subtle">
            <div className="flex items-start gap-4">
              <Avatar name="Beatriz Mendes Oliveira" size="lg" tone="blue" />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <Badge tone={TIPO.presencial}>{TIPO.presencial.label}</Badge>
                  <Badge tone={STATUS.confirmada} dot>{STATUS.confirmada.label}</Badge>
                </div>
                <h2 className="font-heading text-xl font-semibold">Beatriz Mendes Oliveira</h2>
                <div className="flex items-center gap-3 text-xs text-muted mt-1">
                  <span>34 anos</span>
                  <span>·</span>
                  <span>(11) 98139-1174</span>
                  <span>·</span>
                  <span>beatriz.mendes@email.com</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] text-muted uppercase tracking-wider font-bold">Horário</div>
                <div className="font-heading text-2xl font-semibold tabular-nums">09:00</div>
                <div className="text-xs text-muted">→ 09:30</div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white border border-soft rounded-xl shadow-subtle">
            <div className="border-b border-soft px-6 pt-4">
              <div className="flex gap-1 overflow-x-auto">
                {['Anamnese', 'Exames', 'Receituário', 'Orientações', 'Histórico'].map((t, i) => (
                  <button key={t} className={`px-4 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${
                    i === 0 ? 'border-brand text-brand' : 'border-transparent text-muted hover:text-[#171D1A]'
                  }`}>{t}</button>
                ))}
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <div className="text-[10px] text-muted uppercase tracking-wider font-bold mb-1">Queixa Principal</div>
                <p className="text-sm text-[#171D1A] leading-relaxed">Paciente relata desconforto pélvico há aproximadamente 3 semanas, com piora durante atividade física.</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[10px] text-muted uppercase tracking-wider font-bold mb-1">Pressão Arterial</div>
                  <p className="font-heading text-lg font-semibold">120/80 mmHg</p>
                </div>
                <div>
                  <div className="text-[10px] text-muted uppercase tracking-wider font-bold mb-1">Frequência Cardíaca</div>
                  <p className="font-heading text-lg font-semibold">72 bpm</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Coluna direita: Financeiro + ações */}
        <div className="space-y-5">
          {/* Financial card destacado */}
          <div className="bg-[#F8FAFC] border border-soft rounded-xl p-6 shadow-subtle">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-white" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-base font-semibold">Financeiro</h3>
              </div>
              <Badge tone={{ bg: 'bg-amber-100', text: 'text-amber-800', ring: 'ring-amber-200/40' }}>Parcial</Badge>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-5">
              {[
                { label: 'Total', val: 'R$ 600' },
                { label: 'Recebido', val: 'R$ 320', tone: 'text-amber-700' },
                { label: 'Restante', val: 'R$ 280', tone: 'text-muted' },
              ].map((s, i) => (
                <div key={i} className="bg-white rounded-lg p-3 border border-soft text-center">
                  <div className="text-[9px] text-muted uppercase tracking-wider font-bold">{s.label}</div>
                  <div className={`font-heading text-lg font-bold tabular-nums mt-0.5 ${s.tone || 'text-[#171D1A]'}`}>{s.val}</div>
                </div>
              ))}
            </div>

            {/* Progress */}
            <div className="mb-4">
              <div className="flex items-center justify-between text-[10px] mb-1.5">
                <span className="text-muted uppercase tracking-wider font-bold">Progresso</span>
                <span className="font-bold text-amber-700">53%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-amber-500" style={{ width: '53%' }}></div>
              </div>
            </div>

            <button className="w-full bg-brand text-white py-2.5 rounded-lg text-sm font-medium hover:bg-[#275340] flex items-center justify-center gap-1.5">
              <Plus className="w-4 h-4" strokeWidth={1.5}/>
              Novo Lançamento
            </button>

            <div className="border-t border-soft mt-5 pt-4 space-y-2">
              {[
                { data: '24/04/2026', forma: 'PIX', valor: 'R$ 200' },
                { data: '20/04/2026', forma: 'Cartão', valor: 'R$ 120' },
              ].map((l, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <div>
                    <div className="font-medium text-[#171D1A]">{l.data}</div>
                    <div className="text-[10px] text-muted">{l.forma}</div>
                  </div>
                  <span className="font-bold text-[#047857] tabular-nums">{l.valor}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Quick actions */}
          <div className="bg-white border border-soft rounded-xl p-5 shadow-subtle">
            <h3 className="font-heading text-sm font-semibold mb-3">Ações</h3>
            <div className="space-y-2">
              <button className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-brand-soft transition-colors">
                <MessageCircle className="w-4 h-4 text-muted" strokeWidth={1.5}/>
                <span className="text-sm">Enviar WhatsApp</span>
              </button>
              <button className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-brand-soft transition-colors">
                <Activity className="w-4 h-4 text-muted" strokeWidth={1.5}/>
                <span className="text-sm">Marcar como Realizada</span>
              </button>
              <button className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-red-50 transition-colors text-red-700">
                <Plus className="w-4 h-4 rotate-45" strokeWidth={1.5}/>
                <span className="text-sm">Cancelar Consulta</span>
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
);

// ===================== Mockup 4: PACIENTES =====================
const PacientesMockup = () => (
  <div className="flex h-full mockup-root mockup-bg">
    <Sidebar active="pacientes" />
    <div className="flex-1 flex flex-col overflow-hidden">
      <TopBar title="Pacientes" subtitle="342 pacientes ativos · 12 cadastrados esse mês" />
      <main className="flex-1 overflow-y-auto p-8">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="w-3.5 h-3.5 text-muted absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5}/>
              <input type="text" placeholder="Buscar por nome, CPF, telefone..." className="pl-9 pr-3 py-2 text-sm bg-white border border-soft rounded-lg w-80 focus:outline-none focus:ring-2 focus:ring-[#1C3F30]/20 focus:border-[#1C3F30] placeholder:text-slate-400"/>
            </div>
            <div className="inline-flex bg-brand-soft rounded-lg p-1">
              <button className="px-3 py-1 text-xs font-semibold rounded-md bg-white text-brand shadow-sm">Todos</button>
              <button className="px-3 py-1 text-xs font-semibold rounded-md text-muted">Ativos</button>
              <button className="px-3 py-1 text-xs font-semibold rounded-md text-muted">Inativos</button>
            </div>
          </div>
          <button className="bg-brand text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#275340] flex items-center gap-1.5 shadow-sm">
            <Plus className="w-4 h-4" strokeWidth={1.5}/>
            Novo Paciente
          </button>
        </div>

        <div className="bg-white border border-soft rounded-xl overflow-hidden shadow-subtle">
          <div className="bg-[#FDFDFD] border-b border-soft grid grid-cols-12 gap-4 px-6 py-3">
            {['Paciente', 'Contato', 'Última Consulta', 'Status'].map((h, i) => (
              <div key={h} className={`text-[10px] font-bold uppercase tracking-[0.08em] text-muted ${i===0 ? 'col-span-4' : i===1 ? 'col-span-3' : i===2 ? 'col-span-3' : 'col-span-2'}`}>{h}</div>
            ))}
          </div>
          <div className="divide-y divide-[#E6E8E6]">
            {[
              { nome: 'Beatriz Mendes Oliveira', tel: '(11) 98139-1174', tone: 'blue', last: '23/04/2026', status: 'confirmada' },
              { nome: 'Carla Souza Lima', tel: '(11) 99432-5566', tone: 'indigo', last: '15/04/2026', status: 'realizada' },
              { nome: 'Helena Martins Barbosa', tel: '(11) 97788-1212', tone: 'rose', last: '08/04/2026', status: 'realizada' },
              { nome: 'Mariana Dias Cardoso', tel: '(11) 95544-7788', tone: 'brand', last: '03/04/2026', status: 'confirmada' },
              { nome: 'Daniela Costa Pereira', tel: '(11) 99887-1144', tone: 'blue', last: '28/03/2026', status: 'realizada' },
            ].map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-4 px-6 py-3.5 items-center hover:bg-[#FDFDFD] cursor-pointer transition-colors group">
                <div className="col-span-4 flex items-center gap-3">
                  <Avatar name={p.nome} size="md" tone={p.tone}/>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">{p.nome}</div>
                    <div className="text-[10px] text-muted">CPF · 123.456.789-{i}{i}</div>
                  </div>
                </div>
                <div className="col-span-3 text-xs text-muted tabular-nums">{p.tel}</div>
                <div className="col-span-3 text-xs text-muted">{p.last}</div>
                <div className="col-span-2"><Badge tone={STATUS[p.status]} dot>{STATUS[p.status].label}</Badge></div>
              </div>
            ))}
          </div>
          <div className="px-6 py-3 border-t border-soft flex items-center justify-between text-xs text-muted">
            <span>Mostrando 1–5 de 342</span>
            <div className="flex items-center gap-2">
              <button className="p-1 rounded hover:bg-brand-soft"><ChevronLeft className="w-3.5 h-3.5" strokeWidth={1.5}/></button>
              <span className="font-semibold">1 / 69</span>
              <button className="p-1 rounded hover:bg-brand-soft"><ChevronRight className="w-3.5 h-3.5" strokeWidth={1.5}/></button>
            </div>
          </div>
        </div>
      </main>
    </div>
  </div>
);

// ===================== Mockup 5: MOBILE AGENDA =====================
const MobileAgendaMockup = () => (
  <div className="mockup-root mockup-bg h-full overflow-y-auto">
    {/* Mobile top */}
    <header className="sticky top-0 bg-white/85 backdrop-blur-md border-b border-soft px-5 py-3 z-10">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] text-muted uppercase tracking-wider font-bold">QUI · 30 ABR</div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">Agenda</h1>
        </div>
        <div className="flex items-center gap-2">
          <button className="w-9 h-9 rounded-full bg-brand-soft flex items-center justify-center"><Search className="w-4 h-4 text-muted" strokeWidth={1.5}/></button>
          <button className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center shadow-sm"><Plus className="w-4 h-4" strokeWidth={1.5}/></button>
        </div>
      </div>
      {/* Day strip */}
      <div className="flex items-center justify-between gap-1.5 -mx-1">
        {['28', '29', '30', '01', '02'].map((d, i) => {
          const isToday = i === 2;
          const dayLabel = ['SEG', 'TER', 'QUI', 'SEX', 'SÁB'][i];
          return (
            <button key={i} className={`flex-1 flex flex-col items-center gap-0.5 py-2 rounded-xl ${isToday ? 'bg-brand text-white' : 'text-muted hover:bg-brand-soft'}`}>
              <span className="text-[9px] uppercase tracking-wider font-bold">{dayLabel}</span>
              <span className={`font-heading text-base font-semibold ${isToday ? 'text-white' : 'text-[#171D1A]'}`}>{d}</span>
            </button>
          );
        })}
      </div>
    </header>

    {/* Cards */}
    <div className="p-5 space-y-3 pb-24">
      <div className="text-[10px] text-muted uppercase tracking-wider font-bold mb-1">Manhã</div>
      {[
        { hora: '09:00', nome: 'Beatriz Mendes Oliveira', tipo: 'presencial', status: 'confirmada', proc: 'Consulta Ginecológica', valor: 'R$ 320' },
        { hora: '10:30', nome: 'Carla Souza Lima', tipo: 'online', status: 'agendada', proc: 'Reposição Hormonal' },
      ].map((c, i) => (
        <div key={i} className={`${TIPO[c.tipo].bg} ring-1 ${TIPO[c.tipo].ring} rounded-2xl p-4`}>
          <div className="flex items-start gap-3">
            <Avatar name={c.nome} size="md" tone={c.tipo === 'online' ? 'indigo' : 'blue'}/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-heading text-sm font-bold tabular-nums text-[#171D1A]">{c.hora}</span>
                <Badge tone={STATUS[c.status]} dot>{STATUS[c.status].label}</Badge>
              </div>
              <div className="text-sm font-semibold text-[#171D1A] truncate">{c.nome}</div>
              <div className="text-[11px] text-muted truncate">{c.proc}</div>
              <div className="flex items-center gap-2 mt-2">
                <Badge tone={TIPO[c.tipo]}>{TIPO[c.tipo].label}</Badge>
                {c.valor && <span className="text-[11px] font-bold text-[#047857] tabular-nums ml-auto">{c.valor}</span>}
              </div>
            </div>
          </div>
        </div>
      ))}
      <div className="text-[10px] text-muted uppercase tracking-wider font-bold mb-1 mt-4">Tarde</div>
      {[
        { hora: '14:00', nome: 'Helena Martins Barbosa', tipo: 'retorno', status: 'confirmada', proc: 'Retorno · Consulta' },
        { hora: '15:30', nome: 'Mariana Dias Cardoso', tipo: 'presencial', status: 'confirmada', proc: 'Preventivo', valor: 'R$ 250' },
      ].map((c, i) => (
        <div key={i} className={`${TIPO[c.tipo].bg} ring-1 ${TIPO[c.tipo].ring} rounded-2xl p-4`}>
          <div className="flex items-start gap-3">
            <Avatar name={c.nome} size="md" tone={c.tipo === 'retorno' ? 'rose' : 'blue'}/>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="font-heading text-sm font-bold tabular-nums text-[#171D1A]">{c.hora}</span>
                <Badge tone={STATUS[c.status]} dot>{STATUS[c.status].label}</Badge>
              </div>
              <div className="text-sm font-semibold text-[#171D1A] truncate">{c.nome}</div>
              <div className="text-[11px] text-muted truncate">{c.proc}</div>
              <div className="flex items-center gap-2 mt-2">
                <Badge tone={TIPO[c.tipo]}>{TIPO[c.tipo].label}</Badge>
                {c.valor && <span className="text-[11px] font-bold text-[#047857] tabular-nums ml-auto">{c.valor}</span>}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Bottom nav */}
    <nav className="fixed bottom-0 left-0 right-0 bg-white/85 backdrop-blur-xl border-t border-soft px-2 py-2 flex items-center justify-around max-w-[420px] mx-auto rounded-t-2xl shadow-elevated">
      {[
        { icon: LayoutDashboard, label: 'Início' },
        { icon: Calendar, label: 'Agenda', active: true },
        { icon: Users, label: 'Pacientes' },
        { icon: Settings, label: 'Mais' },
      ].map((it, i) => {
        const Icon = it.icon;
        return (
          <button key={i} className={`flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-lg ${it.active ? 'text-brand' : 'text-muted'}`}>
            <Icon className="w-5 h-5" strokeWidth={1.5}/>
            <span className="text-[9px] uppercase tracking-wider font-bold">{it.label}</span>
          </button>
        );
      })}
    </nav>
  </div>
);

// ===================== PAGE =====================
export default function AppMockupsPage() {
  const [active, setActive] = useState('dashboard');

  const screens = [
    { key: 'dashboard', label: 'Dashboard (Desktop)', component: <DashboardMockup /> },
    { key: 'agenda', label: 'Agenda (Desktop)', component: <AgendaMockup /> },
    { key: 'detalhes', label: 'Detalhes Consulta (Desktop)', component: <DetalhesMockup /> },
    { key: 'pacientes', label: 'Pacientes (Desktop)', component: <PacientesMockup /> },
    { key: 'mobile', label: 'Agenda (Mobile)', component: <MobileAgendaMockup /> },
  ];

  const activeScreen = screens.find(s => s.key === active);
  const isMobile = active === 'mobile';

  return (
    <>
      {FONT_LINK}
      <div className="min-h-screen bg-slate-100 p-6">
        <div className="max-w-[1500px] mx-auto">
          <header className="mb-6">
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900">Premium Design System — Mockups</h1>
            <p className="text-sm text-slate-500 mt-1">Tema "Organic & Earthy" · Deep Forest Green · Outfit + Manrope · Toque editorial e médico premium</p>
          </header>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-5">
            {screens.map(s => (
              <button
                key={s.key}
                onClick={() => setActive(s.key)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                  active === s.key
                    ? 'bg-[#1C3F30] text-white shadow-lg'
                    : 'bg-white text-slate-600 border border-slate-200 hover:border-slate-300'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          {/* Frame */}
          <div className={`rounded-2xl overflow-hidden border-8 border-slate-800 shadow-2xl ${isMobile ? 'mx-auto bg-black' : 'bg-slate-100'}`}
            style={isMobile ? { width: 420, height: 800 } : { height: 720 }}
          >
            {activeScreen.component}
          </div>

          <p className="text-xs text-slate-400 mt-4 text-center">
            Estes mockups são apenas para aprovação visual. Se aprovado, o estilo será aplicado ao app real preservando 100% das funcionalidades atuais.
          </p>
        </div>
      </div>
    </>
  );
}
